import { useState, useEffect, useMemo } from "react";
import { Clock, AlertTriangle, CheckCircle } from "lucide-react";

const CURRENT_YEAR = new Date().getFullYear();

export default function CountdownTimer({ deadline, label = "Date limite" }) {
  // Default deadline is March 31st of current year - memoized to prevent infinite re-renders
  const targetDeadline = useMemo(() => {
    if (deadline) {
      return new Date(deadline);
    }
    return new Date(CURRENT_YEAR, 2, 31, 23, 59, 59);
  }, [deadline]);
  
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = targetDeadline - now;

      if (diff <= 0) {
        setIsOverdue(true);
        const absDiff = Math.abs(diff);
        return {
          days: Math.floor(absDiff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((absDiff % (1000 * 60)) / 1000),
        };
      }

      setIsOverdue(false);
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDeadline]);

  const formatNumber = (num) => String(num).padStart(2, "0");

  if (isOverdue) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700 mb-2">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-semibold">Délai dépassé</span>
        </div>
        <div className="flex gap-2">
          <div className="bg-red-100 rounded-lg p-2 text-center min-w-[60px]">
            <div className="text-2xl font-bold text-red-700">{timeLeft.days}</div>
            <div className="text-xs text-red-600">jours</div>
          </div>
          <div className="bg-red-100 rounded-lg p-2 text-center min-w-[60px]">
            <div className="text-2xl font-bold text-red-700">{formatNumber(timeLeft.hours)}</div>
            <div className="text-xs text-red-600">heures</div>
          </div>
          <div className="bg-red-100 rounded-lg p-2 text-center min-w-[60px]">
            <div className="text-2xl font-bold text-red-700">{formatNumber(timeLeft.minutes)}</div>
            <div className="text-xs text-red-600">min</div>
          </div>
        </div>
        <p className="text-sm text-red-600 mt-2">
          Des pénalités s'appliquent pour les déclarations en retard.
        </p>
      </div>
    );
  }

  // Determine urgency level
  const totalHours = timeLeft.days * 24 + timeLeft.hours;
  let bgColor = "bg-emerald-50";
  let borderColor = "border-emerald-200";
  let textColor = "text-emerald-700";
  let boxColor = "bg-emerald-100";
  let boxTextColor = "text-emerald-700";
  let Icon = CheckCircle;

  if (totalHours < 24) {
    bgColor = "bg-red-50";
    borderColor = "border-red-200";
    textColor = "text-red-700";
    boxColor = "bg-red-100";
    boxTextColor = "text-red-700";
    Icon = AlertTriangle;
  } else if (totalHours < 72) {
    bgColor = "bg-orange-50";
    borderColor = "border-orange-200";
    textColor = "text-orange-700";
    boxColor = "bg-orange-100";
    boxTextColor = "text-orange-700";
    Icon = AlertTriangle;
  } else if (timeLeft.days < 30) {
    bgColor = "bg-amber-50";
    borderColor = "border-amber-200";
    textColor = "text-amber-700";
    boxColor = "bg-amber-100";
    boxTextColor = "text-amber-700";
    Icon = Clock;
  }

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
      <div className={`flex items-center gap-2 ${textColor} mb-2`}>
        <Icon className="w-5 h-5" />
        <span className="font-semibold">{label}</span>
      </div>
      <div className="flex gap-2">
        <div className={`${boxColor} rounded-lg p-2 text-center min-w-[60px]`}>
          <div className={`text-2xl font-bold ${boxTextColor}`}>{timeLeft.days}</div>
          <div className={`text-xs ${textColor}`}>jours</div>
        </div>
        <div className={`${boxColor} rounded-lg p-2 text-center min-w-[60px]`}>
          <div className={`text-2xl font-bold ${boxTextColor}`}>{formatNumber(timeLeft.hours)}</div>
          <div className={`text-xs ${textColor}`}>heures</div>
        </div>
        <div className={`${boxColor} rounded-lg p-2 text-center min-w-[60px]`}>
          <div className={`text-2xl font-bold ${boxTextColor}`}>{formatNumber(timeLeft.minutes)}</div>
          <div className={`text-xs ${textColor}`}>min</div>
        </div>
        <div className={`${boxColor} rounded-lg p-2 text-center min-w-[60px]`}>
          <div className={`text-2xl font-bold ${boxTextColor}`}>{formatNumber(timeLeft.seconds)}</div>
          <div className={`text-xs ${textColor}`}>sec</div>
        </div>
      </div>
    </div>
  );
}
